import { useState, useEffect, useRef } from "react";
import { Outlet, createRootRoute } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { useAppStore } from "@/lib/store";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { CheckCircle, User } from "lucide-react";

const EARN_MESSAGES = [
  { name: "Brian Mwangi", amount: "2.00" },
  { name: "Liam O'Connor", amount: "3.90" },
  { name: "Peter Kipchoge", amount: "3.20" },
  { name: "Sarah Muthoni", amount: "4.10" },
  { name: "John Smith", amount: "5.20" },
  { name: "Maria Garcia", amount: "6.10" },
  { name: "James Njoroge", amount: "6.75" },
  { name: "Lucy Atieno", amount: "1.95" },
  { name: "Michael Omondi", amount: "3.80" },
  { name: "Lucas Müller", amount: "5.90" },
  { name: "Mary Jebet", amount: "2.40" },
  { name: "David Kiprono", amount: "4.60" },
  { name: "Anna Salome", amount: "7.10" },
  { name: "Sofia Rossi", amount: "4.30" },
  { name: "Carlos Mendes", amount: "6.40" },
  { name: "Yuki Tanaka", amount: "3.60" },
  { name: "Fatima Al-Farsi", amount: "5.75" },
  { name: "Grace Wanjiku", amount: "5.50" },
  { name: "Joseph Mwangi", amount: "3.30" },
  { name: "Chen Wei", amount: "2.70" },
  { name: "Esther Wanjiru", amount: "5.90" },
  { name: "Aisha Khan", amount: "4.80" },
];

interface ChatMessage {
  id: number;
  name: string;
  amount: string;
  timestamp: number;
}

export const Route = createRootRoute({
  head: () => ({
    meta: [{ name: "fameswap-verification", content: "LJEjvgk57AAzNPaM02cw0UqyaX2SCjAY" }],
  }),
  component: RootComponent,
});

function RootComponent() {
  const initAuth = useAppStore((s) => s.initAuth);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const indexRef = useRef(0);
  const toastIdRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    const showNext = () => {
      const msg = EARN_MESSAGES[indexRef.current % EARN_MESSAGES.length];
      indexRef.current += 1;

      const id = ++toastIdRef.current;
      const newMessage: ChatMessage = {
        id,
        name: msg.name,
        amount: msg.amount,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev.slice(-2), newMessage]);

      timeoutRef.current = setTimeout(() => {
        setMessages((prev) => prev.filter((m) => m.id !== id));
        queueRef.current = setTimeout(showNext, 2000);
      }, 4000);
    };

    queueRef.current = setTimeout(showNext, 3000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (queueRef.current) clearTimeout(queueRef.current);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden pt-16">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />

      <div className="fixed top-16 right-4 z-40 flex max-h-[60vh] w-72 flex-col gap-2 overflow-hidden pointer-events-none">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className="animate-in fade-in slide-in-from-top-2 pointer-events-auto flex items-center gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-slate-700 shadow-md"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100">
              <User className="h-3.5 w-3.5 text-emerald-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-slate-800">{msg.name}</p>
              <p className="text-[11px] text-slate-500">
                Earned <span className="font-bold text-emerald-600">$ {msg.amount}</span>
              </p>
            </div>
            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          </div>
        ))}
      </div>

      <Toaster position="top-center" richColors duration={2000} />
    </div>
  );
}
